/**
 * Phase gating (R18, design/11 §1): everything that talks to an outside platform sits behind a
 * feature flag that is OFF in phase 1. The admin shows those places as a "coming soon" card — never
 * an error — and makes no call to their endpoints.
 */
export const FEATURE_FLAGS = [
  "payments.stripe",
  "payments.stripe_connect",
  "channels.ebay",
  "channels.whatnot",
  "channels.mercari",
  "channels.tiktok_shop",
  "tax.nta_api",
  "shipping.shipandco",
  "shipping.zonos",
] as const;

export type FeatureFlag = (typeof FEATURE_FLAGS)[number];

export type FeatureFlags = Readonly<Record<FeatureFlag, boolean>>;

/** Phase 1: all off (the owner switches them on when T20–T23 start — R18). */
export const PHASE1_FLAGS: FeatureFlags = Object.fromEntries(
  FEATURE_FLAGS.map((flag) => [flag, false]),
) as unknown as FeatureFlags;

/** The epic that connects the feature (shown on the card). */
export const FEATURE_EPIC: Readonly<Record<FeatureFlag, string>> = {
  "payments.stripe": "T20",
  "payments.stripe_connect": "T20",
  "channels.ebay": "T21",
  "channels.whatnot": "T22",
  "channels.mercari": "T22",
  "channels.tiktok_shop": "T22",
  "tax.nta_api": "T23",
  "shipping.shipandco": "T23",
  "shipping.zonos": "T23",
};

export function isFeatureFlag(value: unknown): value is FeatureFlag {
  return typeof value === "string" && (FEATURE_FLAGS as readonly string[]).includes(value);
}

/**
 * Server answer → flags. Unknown keys are ignored, missing ones stay off; a flag is on only when
 * the server says exactly `true`.
 */
export function parseFlags(body: unknown): FeatureFlags {
  const source =
    body && typeof body === "object" && "features" in body
      ? (body as { features: unknown }).features
      : body;
  const out: Record<string, boolean> = { ...PHASE1_FLAGS };
  if (source && typeof source === "object") {
    for (const [key, value] of Object.entries(source as Record<string, unknown>)) {
      if (isFeatureFlag(key)) {
        out[key] = value === true;
      }
    }
  }
  return out as unknown as FeatureFlags;
}

/**
 * Every place design/04 names Stripe / eBay / Whatnot / Mercari (T11-09 checklist) and the flag
 * behind it. `where` is the screen that renders the card.
 */
export const COMING_SOON_SPOTS = [
  { id: "settings_payments_stripe", feature: "payments.stripe", where: "/settings/payments" },
  { id: "settings_channels_ebay", feature: "channels.ebay", where: "/settings/channels" },
  { id: "settings_channels_whatnot", feature: "channels.whatnot", where: "/settings/channels" },
  { id: "settings_channels_mercari", feature: "channels.mercari", where: "/settings/channels" },
  { id: "orders_payment_link", feature: "payments.stripe", where: "/orders/drafts" },
  {
    id: "consignor_payout_stripe_connect",
    feature: "payments.stripe_connect",
    where: "/consignment/payouts",
  },
] as const satisfies ReadonlyArray<{ id: string; feature: FeatureFlag; where: string }>;
